import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { TextField, Autocomplete, Chip, Checkbox, Box, Button, Divider, Typography, AutocompleteProps, FormControlLabel, InputAdornment } from "@mui/material";
import { FixedSizeList as List } from 'react-window';
import { useTheme } from '@emotion/react';
import { Controller, useFormContext } from 'react-hook-form';

interface Item {
  id: string;
  label: string;
  [key: string]: any;
}

interface GenericInfiniteScrollListProps {
  items: Item[];
  selectedItems?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  onOnlyItem?: (itemId: string) => void;
  searchPlaceholder?: string;
  itemHeight?: number;
  listHeight?: number;
  maxTagWidth?: number;
  showSelectAll?: boolean;
  showOnlyButton?: boolean;
  showItemCount?: boolean;
  customItemRenderer?: (item: Item, isSelected: boolean) => React.ReactNode;
  customTagRenderer?: (item: Item) => React.ReactNode;
  customSearchFilter?: (item: Item, searchValue: string) => boolean;
  freeSolo?: boolean;
  defaultGroup?: string;
}

const ListboxComponent = React.memo(
  React.forwardRef<HTMLDivElement, any>((props, ref) => {
    const {
      onToggleItem = () => { },
      onOnlyItem = () => { },
      items = [],
      isSearching = false,
      isSelectAllClicked,
      searchValue = '',
      internalSelectedItems = [],
      itemHeight = 40,
      listHeight = 350,
      showOnlyButton = true,
      customItemRenderer,
      isOptionEqualToValue,
      selectedItems: selectedItemsProp,
      getOptionLabel: getOptionLabelProp,
      getOptionKey: getOptionKeyProp,
      pasteItems: pasteItemsProp,
      freeSolo = false,
      setItems,
      ...other
    } = props;

    const listRef = useRef<any>(null);
    const lastScrollOffset = useRef(0);
    const theme: any = useTheme();

    const getOptionLabel = useCallback(getOptionLabelProp ? getOptionLabelProp : (option: Item) => option.label, [getOptionLabelProp]);
    const getOptionKey = useCallback(getOptionKeyProp ? getOptionKeyProp : (option: Item) => option.id, [getOptionKeyProp]);

    // Séparer les éléments sélectionnés et non sélectionnés
    const selectedItems = items.filter((item: Item) => internalSelectedItems.includes(getOptionKey(item)) || selectedItemsProp.includes(getOptionKey(item)) || [...internalSelectedItems, ...selectedItemsProp].some((selected: Item) => isOptionEqualToValue(item, selected)));
    const unselectedItems = items.filter((item: Item) => !internalSelectedItems.includes(getOptionKey(item)) && !selectedItemsProp.includes(getOptionKey(item)) && ![...internalSelectedItems, ...selectedItemsProp].some((selected: Item) => isOptionEqualToValue(item, selected)));

    // Si on recherche, on filtre les deux listes
    const filteredSelectedItems = isSearching ? selectedItems.filter((item: Item) =>
      getOptionLabel(item).toLowerCase().includes(searchValue.toLowerCase()) || pasteItemsProp.includes(getOptionKey(item))
    ) : selectedItems;

    const filteredUnselectedItems = isSearching ? unselectedItems.filter((item: Item) =>
      getOptionLabel(item).toLowerCase().includes(searchValue.toLowerCase()) || pasteItemsProp.includes(getOptionKey(item))
    ) : unselectedItems;

    const allFilteredItems = [...filteredSelectedItems, ...filteredUnselectedItems];

    const height = allFilteredItems.length > 15 ? listHeight : itemHeight * allFilteredItems.length;

    return (
      <Box ref={ref} {...other}>
        <List
          ref={listRef}
          height={height}
          width="100%"
          style={{ overflowX: 'hidden', overflowY: 'scroll', maxHeight: listHeight }}
          itemSize={itemHeight}
          itemCount={allFilteredItems.length}
          overscanCount={5}
          onScroll={({ scrollOffset }) => {
            lastScrollOffset.current = scrollOffset;
          }}
          itemKey={(index) => getOptionKey(allFilteredItems[index])}
        >
          {({ index, style }) => {
            const item: Item = allFilteredItems[index];
            const isSelected = selectedItems.map((item: Item) => getOptionKey(item)).includes(getOptionKey(item)) || [...internalSelectedItems, ...selectedItemsProp].some((selected: Item) => isOptionEqualToValue(item, selected));
            const isPasted = pasteItemsProp.map((item: Item) => getOptionKey(item)).includes(getOptionKey(item));
            const selectedBgColor = theme.palette.mode === "dark" ? '#323c47' : '#f6f7f8';
            const itemKey = getOptionKey(item);
            const itemLabel = getOptionLabel(item);

            // Déterminer si c'est le premier élément non sélectionné (pour afficher le divider)
            const isFirstSelected = isSelected === true &&
              filteredSelectedItems.length > 0 &&
              index === 0;

            const isLastSelected = isSelected === true &&
              filteredSelectedItems.length > 0 &&
              index === filteredSelectedItems.length - 1;

            const borderProps = isFirstSelected ? {
              borderTopLeftRadius: 5,
              borderTopRightRadius: 5,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            } : isLastSelected ? {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              borderBottomLeftRadius: 5,
              borderBottomRightRadius: 5,
            } : {};

            return (
              <Box>
                <Box
                  key={itemKey}
                  className="hovered-item"
                  style={style}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 0.6,
                    position: 'relative',
                    backgroundColor: isSelected || isPasted ? selectedBgColor : 'transparent',
                    borderRadius: borderProps,
                    '&:hover': {
                      backgroundColor: selectedBgColor,
                      '.hover-only-button': {
                        display: 'inline-flex',
                      },
                      cursor: 'pointer'
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onToggleItem(itemKey);
                      }}
                      control={<Checkbox
                        checked={isSelected}
                        size="small"
                        sx={{ pl: 1.5, pr: 0.6 }}
                        onChange={() => onToggleItem(itemKey)}
                      />}
                      label={customItemRenderer ? (
                        customItemRenderer(item, isSelected)
                      ) : (
                        itemLabel
                      )}
                    />
                  </Box>

                  {showOnlyButton && (
                    <Button
                      className="hover-only-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOnlyItem(itemKey);
                      }}
                      variant="contained"
                      color="inherit"
                      size="small"
                      sx={{
                        display: 'none',
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        height: 24,
                        minWidth: 50,
                        padding: 0,
                        justifyContent: 'center',
                        px: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                        Only
                      </Typography>
                    </Button>
                  )}
                </Box>
              </Box>
            );
          }}
        </List>
      </Box>
    );
  })
);


const GenericInfiniteScrollList: React.FC<Partial<AutocompleteProps<Item, boolean, false, boolean, any>> & GenericInfiniteScrollListProps & { name?: string, defaultGroup?: string, setItems?: (items: Item[]) => void, transformValue?: (value: string) => string }> = ({
  items,
  selectedItems: selectedItemsProp,
  onSelectionChange,
  onOnlyItem,
  searchPlaceholder = "Search...",
  itemHeight = 40,
  listHeight = 350,
  maxTagWidth = 50,
  showSelectAll = true,
  showOnlyButton = true,
  showItemCount = true,
  customItemRenderer,
  customTagRenderer,
  customSearchFilter,
  name,
  defaultGroup,
  multiple = true,
  freeSolo = false,
  transformValue = (value: string) => value,
  setItems,
  ...other
}) => {
  const selectedItems = multiple ? (selectedItemsProp || []) : [selectedItemsProp].filter((item) => !!item) as any;
  const [searchValue, setSearchValue] = useState('');
  const [showCounterBox, setShowCounterBox] = useState(false);
  const [isSelectAllClicked, setIsSelectAllClicked] = useState(false);
  const [internalSelectedItems, setInternalSelectedItems] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const getOptionLabel = useCallback(other.getOptionLabel ? other.getOptionLabel : (option: Item) => option.label, []);
  const isOptionEqualToValue = useCallback(other.isOptionEqualToValue ? other.isOptionEqualToValue : (option: Item, value: Item) => option.id === value.id, []);
  const getOptionKey = useCallback(other.getOptionKey ? other.getOptionKey : (option: Item) => option.id, []);

  const [standaloneValues, setStandaloneValues] = useState<string[]>([]);

  const [pasteItems, setPasteItems] = useState<string[]>([]);

  useEffect(() => {
    if (selectedItems.length > 0 && internalSelectedItems.length === 0) {
      setInternalSelectedItems(selectedItems || []);
    }
  }, [selectedItems]);

  const filteredItems = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    return items.filter((item) => {
      if (customSearchFilter) {
        return customSearchFilter(item, q);
      }
      return !q || getOptionLabel(item).toLowerCase().includes(q);
    }).concat(pasteItems as any);
  }, [items, searchValue, customSearchFilter, pasteItems]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setShowCounterBox(value.length > 0);
  };

  const matchingItemsCount = useMemo(() => {
    if (searchValue.trim()) {
      return filteredItems.length;
    }
    return internalSelectedItems.length;
  }, [filteredItems, internalSelectedItems, searchValue]);

  const { control, setValue: _setValue, getValues: _getValues } = useFormContext() || { control: null };

  const getValues = () => {
    if (!control) {
      return other.value || standaloneValues;
    }
    return multiple ? _getValues(name ?? '') : [_getValues(name ?? '')];
  };

  const setValue = (value: any) => {
    if (!control && !other.value) {
      setStandaloneValues((prev) => [...value]);
      return;
    }
    if (!control && other.value) {
      onSelectionChange?.(value);
      return;
    }
    _setValue(name ?? '', value, { shouldValidate: true });
  };

  const render = (field: any) => {
    return (
      <Autocomplete
        {...field}
        value={field.value}
        onChange={(event, newValue) => setValue(newValue)}
        multiple={multiple}
        size={other.size ?? "small"}
        disableCloseOnSelect
        options={filteredItems}
        getOptionLabel={getOptionLabel as any}
        freeSolo={freeSolo ?? false}
        groupBy={other.groupBy}
        isOptionEqualToValue={other.isOptionEqualToValue ?? isOptionEqualToValue}
        renderInput={(params) => {
          if (!multiple) {
            params.InputProps.startAdornment = (<InputAdornment position="start">

              <Chip
                variant="soft"
                className='MuiAutocomplete-tag'
                label={getOptionLabel(getValues()[0])}
                size="small"
              />
            </InputAdornment>
            )
          }
          return (
            <>
              <TextField
                {...params}
                value={searchValue}
                inputProps={{
                  ...params.inputProps,
                  value: searchValue,
                  onPaste: async (event: any) => {
                    let pasteData = null;
                    try {
                      pasteData = await navigator.clipboard.readText();
                    } catch (err) {
                      // Fallback : utiliser event.clipboardData si disponible
                      const clipboardData = event.clipboardData?.getData('text');
                      if (clipboardData) {
                        pasteData = clipboardData;
                      }
                    }
                    if (pasteData) {
                      pasteData = pasteData.split('\n');
                      const pasteItems = pasteData.flatMap((item: string) => {
                        return item.trim().split(',');
                      }).map((item: string) => {
                        return item.trim();
                      })
                        .map((pasteItem: string) => {
                          const pasteItemLower = pasteItem.toLowerCase();
                          return filteredItems.find((item: Item) => {
                            const itemLower = getOptionLabel(item).toLowerCase();
                            const itemKey = getOptionKey(item);
                            return (itemLower === pasteItemLower) || (itemKey === pasteItem);
                          });
                        }).filter((item: Item) => !!item);
                      if (pasteItems.length > 0) {
                        setPasteItems(pasteItems);
                      }
                    }
                  },
                  onChange: (event: any) => {
                    handleSearchChange(event.target.value);
                  },
                  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (freeSolo) {
                      if (event.key === 'Enter' && event.currentTarget.value.trim() !== '') {
                        event.preventDefault();
                        event.stopPropagation();
                        setItems?.([...(items || []), { id: event.currentTarget.value.trim(), label: event.currentTarget.value.trim(), group: defaultGroup || undefined }]);
                        setValue([...(getValues() || []), event.currentTarget.value.trim()]);
                        setSearchValue('');
                      }
                      return;
                    }
                    if (event.key === 'Backspace') {
                      const trimmedSearchValue = searchValue.trim();
                      if (trimmedSearchValue.length !== 0) {
                        event.stopPropagation();
                        if (pasteItems.length > 0) {
                          setSearchValue('');
                          setPasteItems([]);
                          return;
                        }
                      }
                      else {
                        event.preventDefault();
                      }
                    }
                  }
                }}
                fullWidth={other.fullWidth ?? false}
                label={searchPlaceholder}
                inputRef={(el) => {
                  inputRef.current = el;
                }}
              />
            </>
          )
        }}
        ListboxComponent={(props) => (
          <div style={{ position: 'relative' }}>
            {showCounterBox && showSelectAll && (
              <Box sx={{ mt: 1, px: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {showItemCount && (
                    <Typography style={{ fontSize: "14px" }}>
                      {matchingItemsCount} items {isSelectAllClicked ? 'selected' : 'found'}
                    </Typography>
                  )}
                  {isSelectAllClicked ? (
                    <Button
                      onClick={() => {
                        setValue(multiple ? internalSelectedItems : internalSelectedItems[0]);
                        setInternalSelectedItems([]);
                        setIsSelectAllClicked(false);
                        setShowCounterBox(false);
                      }}
                      onMouseDown={(event) => event.preventDefault()}
                      variant="contained"
                      color="inherit"
                      size="small"
                      sx={{ height: 24, minWidth: 50, px: 1 }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 800 }}>Apply</Typography>
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        const alreadySelected = getValues();
                        if (pasteItems.length > 0) {
                          setInternalSelectedItems([...alreadySelected, ...pasteItems.map((item: any) => getOptionKey(item))]);
                          setPasteItems([])
                        }
                        else {
                          setInternalSelectedItems([...alreadySelected, ...filteredItems.map((item) => getOptionKey(item))]);
                        }
                        setSearchValue('');
                        setIsSelectAllClicked(true);
                        setShowCounterBox(true);
                      }}
                      onMouseDown={(event) => event.preventDefault()}
                      variant="contained"
                      color="inherit"
                      size="small"
                      sx={{ height: 24, minWidth: 50, px: 1, whiteSpace: 'nowrap' }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 800 }}>Select All</Typography>
                    </Button>
                  )}
                </Box>
                <Divider sx={{ my: 1 }} />
              </Box>
            )}
            <ListboxComponent
              {...props}
              isSearching={searchValue.trim().length > 0}
              searchValue={searchValue}
              style={{ overflow: 'hidden', maxHeight: listHeight }}
              selectedItems={getValues() || []}
              onToggleItem={(itemId: string) => {
                const currentValues = getValues();
                if (multiple) {
                  const newValues = currentValues.map((id: string) => (id as any).id || id as any).includes(itemId)
                    ? currentValues.filter((id: string) => ((id as any).id || id as any) !== itemId)
                    : [...currentValues, itemId];
                  setValue(newValues.map((id: string) => transformValue(id)));
                }
                else {
                  setValue(transformValue(itemId));
                }
              }}
              ListboxProps={{
                style: {
                  overflow: 'scroll',
                  padding: 0,
                  margin: 0,
                  maxHeight: 'unset',
                },
              }}
              onOnlyItem={(itemId: string) => setValue(multiple ? [transformValue(itemId)] : transformValue(itemId))}
              freeSolo={freeSolo}
              items={items}
              isSelectAllClicked={isSelectAllClicked}
              internalSelectedItems={internalSelectedItems}
              pasteItems={pasteItems}
              itemHeight={itemHeight}
              listHeight={listHeight}
              showOnlyButton={showOnlyButton}
              customItemRenderer={customItemRenderer}
              getOptionKey={getOptionKey}
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={isOptionEqualToValue}
              setItems={setItems}
            />
          </div>
        )}
        renderTags={(value, getTagProps) => {
          value = getValues() || [];
          const limitTags = other.limitTags || 3;
          if (value.length === 0) return null;

          const displayedItems = value.slice(0, limitTags);
          const remainingCount = value.length - limitTags;

          return [
            ...displayedItems.map((item: any, index: number) => {
              const { key, ...tagProps } = getTagProps({ index: index });
              return (
                customTagRenderer ? (
                  customTagRenderer(item)
                ) : (
                  <Chip
                    {...tagProps}
                    key={key}
                    variant="soft"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: maxTagWidth,
                    }}
                    label={getOptionLabel(item)}
                    size="small"
                  />
                ))
            }),
            ...(remainingCount > 0 ? [
              <Chip
                key={name + "+more"}
                label={`+${remainingCount}`}
                size="small"
                variant="soft"
                sx={{ color: '#919eab', ml: 0.3 }}
              />
            ] : []),
          ];
        }}
        {...other}
      />
    )
  }


  if (!control) {
    return render({ value: getValues() });
  }

  return (
    <Controller
      name={name ?? ''}
      control={control}
      render={({ field, fieldState: { error } }) => render(field)}
    />
  );
};

export default GenericInfiniteScrollList;